import { CheckCircle, Download, Error } from '@mui/icons-material';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import IconStatusButton from '../../common-components/icon-status-button/icon-status-button';
import useUserRoleAndShortId from '../../hooks/user-details/useUserDetails';
import { Port } from '../../maritime-menu-options/world-ports-panel/world-ports.model';
import { ProReportResponse, ProVesselTypes } from '../../models/pro.model';
import {
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../utils/analytics';
import DownloadHelpers from '../../utils/download.utils';
import { buildProReportFileName } from '../../utils/filename.utils';
import ResponseDialog from '../pro-report-dialog/response-dialog';
import ProReportExport from './pro-report-pdf-export';

interface ProReportPdfDownloadButtonProps {
  port: Port;
  report: ProReportResponse;
  selectedVesselType: ProVesselTypes;
  fetching: boolean;
}

function ProReportPdfDownloadButton({
  port,
  report,
  selectedVesselType,
  fetching,
}: ProReportPdfDownloadButtonProps): React.ReactElement {
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const userDetails = useUserRoleAndShortId();
  const lastLoggedEvent = useRef<string | null>(null);

  const handleDownload = async () => {
    setDownloadInProgress(true);
    setDownloadComplete(false);
    setDownloadError(false);

    try {
      const pdfFileName = buildProReportFileName(
        port.NAME,
        port.COUNTRY,
        port.UNLOCODE,
        selectedVesselType,
        'pdf'
      );

      await DownloadHelpers.generateAndDownloadPDF(
        pdfFileName,
        <ProReportExport
          report={report}
          portName={port.NAME}
          portCountry={port.COUNTRY}
          portUnlocode={port.UNLOCODE}
        />
      ).then(() => {
        setDownloadComplete(true);
        // Build a unique key for this event to prevent logging multiple analytics events
        // for the same report download. This ensures that if the user downloads the same
        // report instance multiple times, only the first download is logged.
        const eventKey = `${port.UNLOCODE}-${selectedVesselType}-${userDetails.userAnonymisedId}`;
        // Only log if this event is new
        if (lastLoggedEvent.current !== eventKey) {
          logAnalyticsEvent('Ports', 'ProReportPDFDownloaded', [
            getUserAnalyticsEventLabel(userDetails),
          ]);
          lastLoggedEvent.current = eventKey;
        }
      });
    } catch (error) {
      setDownloadError(true);
    } finally {
      setDownloadInProgress(false);
    }
  };

  const tooltipContent = () => (
    <Box>
      <Typography variant="body2">
        This will create and download a PRO report in .pdf format for easy
        sharing and archiving.
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {`The file will be saved to your device's default download location and
        can be opened in most PDF viewers.`}
      </Typography>
    </Box>
  );

  const downloadButtonText = () => {
    if (downloadInProgress) return 'Creating PDF...';
    if (downloadComplete) return 'Downloaded PDF';
    if (downloadError) return 'Error';
    return 'Download PDF';
  };

  const getDownloadIcon = () => {
    if (downloadInProgress) return <CircularProgress size={12} />;
    if (downloadComplete) return <CheckCircle />;
    if (downloadError) return <Error />;
    return <Download />;
  };

  useEffect(() => {
    setDownloadComplete(false);
    setDownloadError(false);
  }, [selectedVesselType, port]);

  return (
    <>
      <IconStatusButton
        tooltipContent={tooltipContent()}
        onClick={handleDownload}
        fullWidth
        statusIcon={getDownloadIcon()}
        statusText={downloadButtonText()}
        disabled={fetching || !report || downloadInProgress}
        dataTestId="pro-report-download-button"
        ariaLabel="Download PRO report"
      />
      <ResponseDialog
        open={downloadError}
        onClose={() => {
          setDownloadComplete(false);
          setDownloadError(false);
        }}
        headerText="PRO Report Download Error"
        titleText="An error occurred while downloading the PRO report for this destination."
        content={
          <>
            <Grid
              container
              spacing={1}
              sx={{ paddingLeft: 2, width: '100%', mb: 1 }}
            >
              <Grid item xs={4}>
                <Typography>Port Name:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>{port.NAME || 'Unknown Port'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>Port Unlocode:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>{port.UNLOCODE || 'Unknown Port Code'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>Vessel Type:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography>
                  {selectedVesselType === ProVesselTypes.NO_VESSEL_TYPE
                    ? 'All Vessels'
                    : selectedVesselType}
                </Typography>
              </Grid>
            </Grid>
            <Typography>
              Please try again later. If the issue persists, contact support.
            </Typography>
          </>
        }
      />
    </>
  );
}

export default ProReportPdfDownloadButton;
